import { useEffect, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AuthLayout } from "@/components/auth/auth-layout";
import { RedirectToLogin } from "@/components/auth/redirect";
import {
  createCompleter,
  outcomeDestination,
  parseCallbackSearch,
  type CallbackOutcome,
} from "@/components/github/logic";
import { api } from "@/lib/api";
import { convex } from "@/lib/convex";
import { useAuthStatus } from "@/lib/use-auth-status";

export const Route = createFileRoute("/github/callback")({
  component: GithubCallbackPage,
});

// One for the life of the page, outside React: what it remembers has to
// outlive a component that strict mode mounts twice. The session's storage
// is reached for at each use, since merely naming it can throw where
// storage is blocked.
const finish = createCompleter(
  (params) => convex.action(api.github.connect.complete, params),
  {
    getItem: (key) => window.sessionStorage.getItem(key),
    setItem: (key, value) => window.sessionStorage.setItem(key, value),
  },
);

/**
 * Where GitHub sends the browser back after an installation or an
 * authorization. The trip can only be finished by the person who started it,
 * so this needs a session: a signed-out arrival goes to log in and comes
 * back to this same address, query string and all.
 */
function GithubCallbackPage() {
  const status = useAuthStatus();
  if (status === "signedOut") return <RedirectToLogin />;
  return (
    <>
      <Working />
      {status === "signedIn" ? <Finish /> : null}
    </>
  );
}

function Working() {
  const { t } = useTranslation("github");
  return (
    <AuthLayout title={t("callback.title")}>
      <p role="status" className="flex items-center gap-2 text-muted-foreground">
        <Loader
          aria-hidden
          strokeWidth={1.75}
          className="size-3.5 shrink-0 animate-[spin_1.2s_linear_infinite]"
        />
        {t("callback.working")}
      </p>
    </AuthLayout>
  );
}

/** Hands what GitHub gave back to the server, once, and leaves with the answer. */
function Finish() {
  const { t } = useTranslation("github");
  const navigate = useNavigate();
  // Read once: the address is about to change.
  const [params] = useState(() => parseCallbackSearch(window.location.search));
  // The page has one thing to do, and does it once however often it renders:
  // what the effect needs besides `params` is read through this.
  const leave = useRef({ navigate, t });
  useEffect(() => {
    leave.current = { navigate, t };
  });

  useEffect(() => {
    let left = false;
    const outcome: Promise<CallbackOutcome> = params
      ? finish(params)
      : // No state at all: there is nothing to hand in.
        Promise.resolve({
          kind: "finished",
          completion: { workspaceSlug: null, result: "error", reason: "state_invalid" },
        });
    void outcome.then((result) => {
      if (left) return;
      const { navigate, t } = leave.current;
      const destination = outcomeDestination(result);
      if (destination.to === "integrations") {
        void navigate({
          to: "/$workspaceSlug/settings/integrations",
          params: { workspaceSlug: destination.workspaceSlug },
          search: destination.search,
          replace: true,
        });
        return;
      }
      // No workspace to return to: the message travels as a toast, which
      // lives above the router and survives the way to the app's root.
      if (destination.reason) {
        toast.error(t("callback.failed"), {
          description: t(`callback.reasons.${destination.reason}`),
          duration: 12_000,
        });
      }
      void navigate({ to: "/", replace: true });
    });
    return () => {
      left = true;
    };
  }, [params]);

  return null;
}
